// BUSINESS DESK — historique : filtres, PDF, WhatsApp, devis→facture, encaissement, annulation.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { deskApi, fmtF, newIdempotencyKey, pdfUrl, TYPE_LABEL, STATUS_LABEL } from "./deskApi";

const API = process.env.REACT_APP_BACKEND_URL || "";

const FILTERS = [
  { id: "", label: "Tous" },
  { id: "QUOTE", label: "📝 Devis" },
  { id: "INVOICE", label: "🧾 Factures" },
  { id: "PAYMENT_RECEIPT", label: "💰 Reçus" },
];

function PayModal({ doc, onClose, onPaid }) {
  const remaining = doc.total_minor - (doc.paid_minor || 0);
  const [amount, setAmount] = useState(String(remaining));
  const [method, setMethod] = useState("Espèces");
  const [note, setNote] = useState("");
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await deskApi("/payments/confirm", {
        method: "POST",
        idempotency: newIdempotencyKey(),
        body: { document_id: doc.document_id, amount_minor: Math.round(parseFloat(amount.replace(",", ".")) || 0), method, confirmation_note: note, confirm: true },
      });
      toast.success(`Reçu ${r.receipt.document_number} généré ✓`);
      onPaid(r.receipt);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="desk-modal-overlay" onClick={() => !busy && onClose()}>
      <div className="desk-modal" onClick={(e) => e.stopPropagation()} data-testid="desk-pay-modal">
        <h3>💰 Encaisser un paiement</h3>
        <p className="dm-sub">Facture {doc.document_number} — restant dû : <b>{fmtF(remaining)}</b></p>
        <div className="desk-field"><label>Montant reçu (FCFA)</label>
          <input inputMode="numeric" className="desk-input" data-testid="desk-pay-amount-input" value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
        <div className="desk-field"><label>Moyen de paiement</label>
          <select className="desk-select" data-testid="desk-pay-method-select" value={method} onChange={(e) => setMethod(e.target.value)}>
            {["Espèces", "Virement bancaire", "Mobile Money", "Chèque"].map((m) => <option key={m}>{m}</option>)}
          </select></div>
        <div className="desk-field"><label>Note (optionnel)</label>
          <input className="desk-input" data-testid="desk-pay-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex : reçu en main propre" /></div>
        <label className="desk-checkbox-row">
          <input type="checkbox" data-testid="desk-pay-confirm-checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
          Je confirme avoir réellement reçu ce paiement. Le reçu officiel sera généré.
        </label>
        <button className="desk-btn desk-btn-gold" style={{ marginTop: 16 }} data-testid="desk-pay-submit-btn" disabled={!checked || busy} onClick={confirm}>
          {busy ? "Confirmation…" : "Confirmer le paiement ✓"}
        </button>
        <button className="desk-btn desk-btn-outline" style={{ marginTop: 8 }} disabled={busy} onClick={onClose}>Annuler</button>
      </div>
    </div>
  );
}

function CancelModal({ doc, onClose, onCancelled }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const cancel = async () => {
    setBusy(true);
    try {
      await deskApi(`/documents/${doc.document_id}/cancel`, { method: "POST", body: { reason } });
      toast.success("Document annulé (conservé dans l'historique).");
      onCancelled();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="desk-modal-overlay" onClick={() => !busy && onClose()}>
      <div className="desk-modal" onClick={(e) => e.stopPropagation()} data-testid="desk-cancel-modal">
        <h3>Annuler {doc.document_number || "ce document"} ?</h3>
        <p className="dm-sub">Le document restera visible avec la mention ANNULÉ. Une justification est obligatoire (audit).</p>
        <div className="desk-field"><label>Motif d'annulation *</label>
          <input className="desk-input" data-testid="desk-cancel-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex : erreur de montant" /></div>
        <button className="desk-btn desk-btn-danger" style={{ marginTop: 16 }} data-testid="desk-cancel-submit-btn" disabled={reason.trim().length < 3 || busy} onClick={cancel}>
          {busy ? "…" : "Confirmer l'annulation"}
        </button>
        <button className="desk-btn desk-btn-outline" style={{ marginTop: 8 }} disabled={busy} onClick={onClose}>Retour</button>
      </div>
    </div>
  );
}

export default function DeskDocuments() {
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [docs, setDocs] = useState([]);
  const [payDoc, setPayDoc] = useState(null);
  const [cancelDoc, setCancelDoc] = useState(null);
  const [issueDoc, setIssueDoc] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (type) params.set("type", type);
    if (q) params.set("q", q);
    deskApi(`/documents?${params}`).then((d) => setDocs(d.documents)).catch(() => {});
  }, [type, q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const shareWhatsApp = async (d) => {
    try {
      const r = await deskApi(`/documents/${d.document_id}/share-link`);
      const link = `${API}${r.path}`;
      const label = TYPE_LABEL[d.document_type].toLowerCase();
      const msg = `Bonjour ${d.customer_snapshot?.name || ""} 👋\nVoici votre ${label} ${d.document_number} d'un montant de ${fmtF(d.total_minor)}.\n📄 Consultez le document ici : ${link}\nMerci de votre confiance !`;
      const digits = String(d.customer_snapshot?.phone || "").replace(/[^0-9]/g, "");
      const wa = digits
        ? `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`
        : `https://wa.me/?text=${encodeURIComponent(msg)}`;
      window.open(wa, "_blank", "noopener");
    } catch (e) { toast.error(e.message); }
  };

  const convertQuote = async (d) => {
    setBusy(true);
    try {
      const r = await deskApi(`/documents/${d.document_id}/convert-to-invoice`, { method: "POST" });
      setIssueDoc({ ...r.document, _fromQuote: d.document_number });
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const issueNow = async () => {
    setBusy(true);
    try {
      const r = await deskApi(`/documents/${issueDoc.document_id}/issue`, {
        method: "POST",
        body: { confirm: true },
        idempotency: newIdempotencyKey(),
      });
      toast.success(`Facture ${r.document.document_number} émise ✓`);
      setIssueDoc(null);
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div data-testid="desk-documents">
      <div className="desk-field">
        <input className="desk-input" data-testid="desk-docs-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="🔍 N° de document ou nom du client…" />
      </div>
      <div className="desk-filters">
        {FILTERS.map((f) => (
          <button key={f.id} className={`desk-chip ${type === f.id ? "on" : ""}`} data-testid={`desk-filter-${f.id || "all"}`} onClick={() => setType(f.id)}>{f.label}</button>
        ))}
      </div>

      {docs.length === 0 && <div className="desk-empty">Aucun document.</div>}
      {docs.map((d) => {
        const remaining = d.document_type === "INVOICE" && d.status === "ISSUED" ? d.total_minor - (d.paid_minor || 0) : null;
        const fullyPaid = remaining !== null && remaining <= 0;
        return (
          <div key={d.document_id} className="desk-doc-card" data-testid={`desk-doc-${d.document_id}`}>
            <div className="desk-doc-head">
              <div>
                <div className="desk-doc-number">{d.document_number || `${TYPE_LABEL[d.document_type]} (brouillon)`}</div>
                <div className="desk-doc-client">{d.customer_snapshot?.name} · {new Date(d.created_at).toLocaleDateString("fr-FR")}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div className="desk-doc-total">{fmtF(d.total_minor)}</div>
                {fullyPaid
                  ? <span className="desk-badge desk-badge-paid">PAYÉE ✓</span>
                  : <span className={`desk-badge desk-badge-${d.status === "ISSUED" ? "issued" : d.status === "CANCELLED" ? "cancelled" : "draft"}`}>{STATUS_LABEL[d.status]}</span>}
              </div>
            </div>
            {remaining !== null && remaining > 0 && (d.paid_minor || 0) > 0 && (
              <div className="desk-note">Payé : {fmtF(d.paid_minor)} — restant : <b>{fmtF(remaining)}</b></div>
            )}
            <div className="desk-doc-actions">
              {(d.document_number || d.status !== "DRAFT") && (
                <a className="desk-btn desk-btn-dark desk-btn-sm" style={{ textDecoration: "none" }} data-testid={`desk-doc-pdf-${d.document_id}`} href={pdfUrl(d.document_id)} target="_blank" rel="noreferrer">📄 PDF</a>
              )}
              {d.status === "ISSUED" && (
                <button className="desk-btn desk-btn-outline desk-btn-sm" style={{ borderColor: "#25D366", color: "#128C4A" }} data-testid={`desk-doc-wa-${d.document_id}`} disabled={busy} onClick={() => shareWhatsApp(d)}>
                  💬 WhatsApp
                </button>
              )}
              {d.document_type === "QUOTE" && d.status === "ISSUED" && !d.meta?.converted_invoice_id && (
                <button className="desk-btn desk-btn-gold desk-btn-sm" data-testid={`desk-doc-convert-${d.document_id}`} disabled={busy} onClick={() => convertQuote(d)}>
                  🧾 → Facture
                </button>
              )}
              {d.document_type === "QUOTE" && d.meta?.converted_invoice_id && (
                <span className="desk-badge desk-badge-paid" style={{ alignSelf: "center" }}>Facturé ✓</span>
              )}
              {d.status === "DRAFT" && (
                <button className="desk-btn desk-btn-gold desk-btn-sm" data-testid={`desk-doc-issue-${d.document_id}`} disabled={busy} onClick={() => setIssueDoc(d)}>
                  Émettre ✓
                </button>
              )}
              {remaining !== null && remaining > 0 && (
                <button className="desk-btn desk-btn-gold desk-btn-sm" data-testid={`desk-doc-pay-${d.document_id}`} onClick={() => setPayDoc(d)}>💰 Encaisser</button>
              )}
              {d.status !== "CANCELLED" && d.document_type !== "PAYMENT_RECEIPT" && (
                <button className="desk-btn desk-btn-danger desk-btn-sm" data-testid={`desk-doc-cancel-${d.document_id}`} onClick={() => setCancelDoc(d)}>Annuler</button>
              )}
            </div>
          </div>
        );
      })}

      {payDoc && <PayModal doc={payDoc} onClose={() => setPayDoc(null)} onPaid={() => { setPayDoc(null); load(); }} />}
      {cancelDoc && <CancelModal doc={cancelDoc} onClose={() => setCancelDoc(null)} onCancelled={() => { setCancelDoc(null); load(); }} />}

      {issueDoc && (
        <div className="desk-modal-overlay" onClick={() => !busy && (setIssueDoc(null), load())}>
          <div className="desk-modal" onClick={(e) => e.stopPropagation()} data-testid="desk-issue-from-list-modal">
            <h3>{issueDoc._fromQuote ? `Facture prête (depuis le devis ${issueDoc._fromQuote})` : "Émettre ce document ?"}</h3>
            <p className="dm-sub">
              {issueDoc.customer_snapshot?.name} — {fmtF(issueDoc.total_minor)}. Un numéro officiel sera attribué à l'émission.
            </p>
            <button className="desk-btn desk-btn-gold" data-testid="desk-issue-from-list-btn" disabled={busy} onClick={issueNow}>
              {busy ? "Émission…" : "Émettre la facture maintenant ✓"}
            </button>
            <button className="desk-btn desk-btn-outline" style={{ marginTop: 8 }} disabled={busy} onClick={() => { setIssueDoc(null); load(); }}>
              Garder en brouillon
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
